import React from 'react';
import ContentWrapper from '../../Layout/ContentWrapper';
import ContentWide from '../../Layout/ContentWide';
import ContentNarrow from '../../Layout/ContentNarrow';
import PageSection from '../../Layout/PageSection';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import Image from '@plone/volto/components/theme/Image/Image';
import ResponsiveComponentSwitch from '../../Atoms/ResponsiveComponentSwitch';

/**
 * TeaserGalleryShowcase: Example page using several components that display teaser galleries in grid, carousel or other layouts
 * 

 */

const baseTeaserProps = {
  url: '',
  label: 'example label',
  caption: 'example caption',
  description: 'example description',
  ctaText: 'example cta text',
  readMoreText: 'example read more text',
  className: '',
  width: '200px',
  tags: ['tag1', 'tag2'],
};

const sampleImageUrls = [
  'https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?q=80&w=1613&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
];

const ExTeaserItems = Array.from({ length: 12 }, (_, i) => (
  <TeaserCardVertical
    imageUrl={sampleImageUrls[0]}
    title={`Example Title ${i + 1}`}
    {...baseTeaserProps}
  />
));

const TeaserGalleryShowcase = () => {
  return (
    <>
      <ContentWrapper>
        <div className="components-showcase__header">
          <h1 className="components-showcase__title">
            Teaser Gallery Showcase
          </h1>
          <p className="components-showcase__description">
            Multimedia component showcase for the ESS Design System based on
            @plone/components
          </p>
        </div>

        {/* Youtube component Section */}
        <section className="components-showcase__section">
          <h2 className="components-showcase__section-title">
            Responsive Teaser listing component
          </h2>
          <p>
            A responsive teaser listing component that renders a grid of teaser
            items, with a configurable number of columns per breakpoint, and
            whose rows are wrapped in a carousel on smaller viewports up to a
            defined breakpoint, after which the full grid is shown without
            carousel behavior.
          </p>
          <p>
            <ul style={{ listStylePosition: 'inside' }}>
              <li>
                sm: up to 767px (small screens, e.g. phones in landscape).
              </li>
              <li>md: 768px and up (medium screens, e.g. tablets).</li>
              <li>lg: 1024px and up (tablets).</li>
              <li>xl: 1280px and up (small laptops).</li>
              <li>xxl: 1440px and up (desktops).</li>
            </ul>
            <p>Examples:</p>
            <ul style={{ listStylePosition: 'inside' }}>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex1">
                  Layout listing showing 1 Teaser Card per row below 768px,
                  switching to 2 cards 768px, 3 cards ≥1024px, and 4 cards
                  ≥1440px.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex2">
                  Layout listing showing 1 Teaser Card per row below 768px,
                  switching to 2 cards 768px and 3 cards ≥1280px.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex3">
                  Layout listing showing 1 Teaser Card per row below 768px,
                  switching to 2 cards 768px and 3 cards ≥1024px.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex4">
                  Layout listing showing 1 Teaser Card per row below 768px,
                  switching to 2 cards 768px and 3 cards ≥1280px.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex5">
                  Large gap example between items.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex6">
                  Carousel of 2 Teaser Card components.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex7">
                  Carousel of one Teaser card component.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex8">
                  Carousel of 2 Image components.
                </a>
              </li>
              <li>
                <a style={{ textDecoration: 'underline' }} href="#ex9">
                  Layout listing showing a carousel below 768px and switching to
                  the teaser listing of example 1 for ≥1024px.
                </a>
              </li>
            </ul>
          </p>
        </section>
        <section className="components-showcase__section" id="ex1">
          <h4>
            ex1: Layout listing showing 1 Teaser Card per row below 768px,
            switching to 2 cards 768px, 3 cards ≥1024px, and 4 cards ≥1440px
          </h4>
          <ResponsiveGrid
            gap="1rem"
            breakpoints={[
              { minWidth: 0, columns: 1 },
              { minWidth: 768, columns: 2 },
              { minWidth: 1024, columns: 3 },
              { minWidth: 1440, columns: 4 },
            ]}
          ></ResponsiveGrid>
        </section>
        <section className="components-showcase__section" id="ex2">
          <h4>
            ex2: News page listing examples - First section with external news.{' '}
          </h4>
          <h4>
            ex2: Layout listing of 8 cards showing 1 Teaser Card per row below
            768px, switching to 2 cards 768px and 3 cards ≥1280px.
          </h4>
          <ResponsiveGrid
            breakpoints={[
              { minWidth: 0, columns: 1 },
              { minWidth: 768, columns: 2 },
              { minWidth: 1280, columns: 4 },
            ]}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <TeaserCardVertical
                imageUrl={sampleImageUrls[0]}
                title={`Example Title ${i + 1}`}
                {...baseTeaserProps}
              />
            ))}
          </ResponsiveGrid>
        </section>
        <section className="components-showcase__section" id="ex3">
          <h4>
            ex3: News page listing examples - First section with internal news.
          </h4>
          <h4>
            Layout listing showing 1 Teaser Card per row below 768px, switching
            to 2 cards 768px and 3 cards ≥1024px.
          </h4>
          <ResponsiveGrid
            breakpoints={[
              { minWidth: 0, columns: 1 },
              { minWidth: 768, columns: 2 },
              { minWidth: 1024, columns: 3 },
            ]}
            gap="1rem"
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <TeaserCardVertical
                imageUrl={sampleImageUrls[0]}
                {...baseTeaserProps}
              />
            ))}
          </ResponsiveGrid>
        </section>
        <section className="components-showcase__section" id="ex4">
          <h4>ex4: Homepage listing examples - Section with internal news.</h4>
          <ResponsiveGrid
            breakpoints={[
              { minWidth: 0, columns: 1 },
              { minWidth: 1024, columns: 3 },
            ]}
            gap="2rem"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <TeaserCardVertical
                imageUrl={sampleImageUrls[0]}
                {...baseTeaserProps}
              />
            ))}
          </ResponsiveGrid>
        </section>
        <section className="components-showcase__section" id="ex5">
          <h4>ex5:LargeGapExample</h4>
          <ResponsiveGrid
            breakpoints={[
              { minWidth: 0, columns: 1 },
              { minWidth: 768, columns: 2 },
              { minWidth: 1024, columns: 3 },
            ]}
            gap="3rem"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <TeaserCardVertical
                imageUrl={sampleImageUrls[0]}
                {...baseTeaserProps}
              />
            ))}
          </ResponsiveGrid>
        </section>
        <section className="components-showcase__section" id="ex6">
          <h4>ex6: Carousel of 2 Teaser Card components.</h4>
          <Carousel>
            <TeaserCardVertical
              width={200}
              imageUrl={sampleImageUrls[0]}
              {...baseTeaserProps}
            />
            <TeaserCardVertical
              width={200}
              imageUrl={sampleImageUrls[1]}
              {...baseTeaserProps}
            />
          </Carousel>
        </section>
        <section className="components-showcase__section" id="ex7">
          <h4>Carousel of one Teaser card component.</h4>
          <Carousel>
            <TeaserCardVertical
              width={200}
              imageUrl={sampleImageUrls[0]}
              {...baseTeaserProps}
            />
          </Carousel>
        </section>
        <section className="components-showcase__section" id="ex8">
          <h4>ex8: Carousel version of the example 1.</h4>
          <Carousel>{ExTeaserItems}</Carousel>
        </section>
        <section className="components-showcase__section" id="ex9">
          <h4>ex9: Carousel of 2 Image components.</h4>
          <Carousel>
            <Image
              src={sampleImageUrls[0]}
              alt="sample alt text"
              className="hero-image"
            />
            <Image
              src={sampleImageUrls[1]}
              alt="sample alt text"
              className="hero-image"
            />
          </Carousel>
        </section>
        <section className="components-showcase__section" id="ex10">
          <h4>ex10: Example 1 but displaying a carousel below 768px</h4>
          <ResponsiveComponentSwitch
            breakpoints={[
              {
                minWidth: 768,
                component: (
                  <ResponsiveGrid
                    breakpoints={[
                      { minWidth: 0, columns: 1 },
                      { minWidth: 768, columns: 2 },
                      { minWidth: 1024, columns: 3 },
                    ]}
                    gap="1rem"
                  >
                    {ExTeaserItems}
                  </ResponsiveGrid>
                ),
              },
            ]}
            defaultComponent={<Carousel>{ExTeaserItems}</Carousel>}
          />
        </section>
        <section className="components-showcase__section" id="ex10">
          <h4>ex11: Responsive grid gap</h4>
          <ResponsiveComponentSwitch
            breakpoints={[
              {
                minWidth: 768,
                component: (
                  <ResponsiveGrid
                    breakpoints={[
                      { minWidth: 0, columns: 1 },
                      { minWidth: 768, columns: 2,  gap: "0.5rem" },
                      { minWidth: 1024, columns: 3,  gap: "1rem" },
                      { minWidth: 1920, columns: 3,  gap: "1.5rem" },
                    ]}
                  >
                    {ExTeaserItems}
                  </ResponsiveGrid>
                ),
              },
            ]}
            defaultComponent={
            <Carousel>
              {ExTeaserItems}
            </Carousel>
            }
          />
        </section>
      </ContentWrapper>
    </>
  );
};

export default TeaserGalleryShowcase;
